import type { SegmentRule } from './segments.js';

/*
 * One ask for notifications (Saif, 25 Oct 2026; CLAUDE.md §7.7, docs/04 §6). The customer app shows its own screen,
 * whose wording names offers, before the phone's Allow box (Apple 4.5.4). Allowing records both `push_updates` (order
 * updates) and `push` (offers and news) as granted, source `notifications_prompt`, with the exact wording shown. Not now
 * records no consent and never opens the phone's box; the screen comes back on Home after `askAgainDays`. A phone that
 * had already allowed notifications before this change gets a one-time version asking about offers only, so that
 * customer's `push` consent is explicit too. Account › Notifications keeps both switches, so offers can be turned off.
 */

export interface NotificationsAskSettings {
  /** Days after a Not now before the screen comes back on Home. */
  askAgainDays: number;
  title: string;
  /** The consent wording: it must name offers, because allowing switches offers on. */
  text: string;
  turnOn: string;
  notNow: string;
  /** The one-time version for a phone that had already allowed notifications. */
  offersTitle: string;
  offersYes: string;
  offersNo: string;
}

export const DEFAULT_NOTIFICATIONS_ASK: NotificationsAskSettings = {
  askAgainDays: 30,
  title: 'Turn on notifications',
  text: 'Get a ping when your order’s ready, plus new flavours, offers and your points first.',
  turnOn: 'Turn on',
  notNow: 'Not now',
  offersTitle: 'Your notifications',
  offersYes: 'Yes, include offers',
  offersNo: 'Just order updates',
};

export const NOTIFICATIONS_ASK_LIMITS = { title: 40, text: 200, button: 28, minDays: 1, maxDays: 365 } as const;

/** The consent wording names offers (or it cannot switch offers on). */
export const namesOffers = (text: string): boolean => /\boffers?\b/i.test(text);

/** What the server logs as "the wording shown": the heading, the text and the button tapped, as one line. */
export function consentWording(title: string, text: string, button: string): string {
  return `${title.trim()}: ${text.trim()} [${button.trim()}]`.slice(0, 400);
}

export type NotificationsAskKind = 'ask' | 'offers';
/** The phone's (or browser's) notification permission, as the customer app reads it. */
export type NotificationPermission = 'default' | 'granted' | 'denied' | 'unsupported' | 'needs_home_screen';

export interface NotificationsAskInput {
  /** Admin › Marketing › Settings: the ask's switch. */
  enabled: boolean;
  signedIn: boolean;
  permission: NotificationPermission;
  /** Where: Home (after joining, and again N days after a Not now) or the order's status page (after an order). */
  place: 'home' | 'status';
  /** This visit started with joining (and the name sheet is done). In memory only. */
  justJoined: boolean;
  /** This device has a live subscription (for the one-time offers version). */
  subscribed: boolean;
  /** An explicit choice about offers by push is on record (any push consent row except the old sign-up default). */
  offersDecided: boolean;
  /** The last Not now, from the server. */
  notNowAt: string | null;
  askAgainDays: number;
  now: Date;
  /** Already shown in this visit: never twice. */
  shownThisVisit: boolean;
}

const DAY = 86_400_000;

/** Whether the last Not now is still holding the screen back. */
export function notNowHolding(notNowAt: string | null, askAgainDays: number, now: Date): boolean {
  if (!notNowAt) return false;
  const t = new Date(notNowAt).getTime();
  return Number.isFinite(t) && now.getTime() - t < askAgainDays * DAY;
}

/** Which screen to show now, if any. */
export function notificationsAsk(i: NotificationsAskInput): NotificationsAskKind | null {
  if (!i.enabled || !i.signedIn || i.shownThisVisit) return null;
  if (notNowHolding(i.notNowAt, i.askAgainDays, i.now)) return null;
  // Allowed before this change: ask about offers once, on Home, while the device is subscribed.
  if (i.permission === 'granted') return i.place === 'home' && i.subscribed && !i.offersDecided ? 'offers' : null;
  // The phone's box can be shown only while undecided; denied, unsupported or not on the home screen: never.
  if (i.permission !== 'default') return null;
  if (i.place === 'status') return 'ask';
  // Home: right after joining, and again once a Not now has run its days.
  return i.justJoined || i.notNowAt ? 'ask' : null;
}

/** Sources that are not an explicit offers choice: the old sign-up default and imports. */
export const UNDECIDED_CONSENT_SOURCES = ['signup', 'import'] as const;

/* ---------- reach (Admin › Marketing › Overview) ---------- */

export type ReachChannel = 'push' | 'email' | 'sms';
export interface ReachDef {
  channel: ReachChannel;
  label: string;
  /** Who could be sent to on this channel at all (allowed notifications, has an email; every member has a mobile). */
  base: SegmentRule[];
  baseLabel: string;
  /** Of those, who has marketing on. */
  on: SegmentRule[];
  onLabel: string;
  /** How the opt-in rate reads: "79% of those who allowed". */
  rateOf: string;
}

/** The segments behind each reach number, so the counts and the linked segments always agree. */
export const REACH: readonly ReachDef[] = [
  { channel: 'push', label: 'Push', base: [{ type: 'push', subscribed: true }], baseLabel: 'allowed notifications', on: [{ type: 'push', subscribed: true }, { type: 'consent', channel: 'push', granted: true }], onLabel: 'offers on', rateOf: 'of those who allowed' },
  { channel: 'email', label: 'Email', base: [{ type: 'has_email', value: true }], baseLabel: 'have an email', on: [{ type: 'has_email', value: true }, { type: 'consent', channel: 'email', granted: true }], onLabel: 'offers on', rateOf: 'of those with an email' },
  { channel: 'sms', label: 'Texts', base: [], baseLabel: 'members', on: [{ type: 'consent', channel: 'sms', granted: true }], onLabel: 'offers on', rateOf: 'of members' },
];

/** Whole-number percent, 0 when there is nobody to ask. */
export const optInRate = (on: number, base: number): number => (base > 0 ? Math.round((100 * on) / base) : 0);
